import React from "react";
import { cn } from "@core/lib/utils";
import { Switch } from "@core/components/ui/switch";

export type LabeledSwitchProps = React.ComponentPropsWithoutRef<typeof Switch> & {
  label?: string | React.ReactNode;
  labelClassName?: string;
  switchClassName?: string;
};

/**
 * The LabeledSwitch component is a customizable switch element that includes a label.
 * This component allows you to style both the switch and the label independently with switchClassName and labelClassName, respectively.
 * @example
 * ```tsx
 * <LabeledSwitch label="Active"
 *    name="active"
 *    id={`isActive-${record.Id}`}
 *    aria-checked={record.IsActive}
 *    defaultChecked={record.IsActive}
 *    onCheckedChange={(checked) => onToggleChange(checked, "IsActive", record.Id)}
 *    {...} />
 * ```
 * They also support children, which are either displayed right of the switch or can be absolute:
 * ```tsx
 * <LabeledSwitch label="Active" name="active" {...} />
 *   <div className="absolute p-2 right-0">
 *     <FontAwesomeIcon icon={faEye} />
 *   </div>
 * </LabeledSwitch>
 * ```
 */
export const LabeledSwitch = React.forwardRef<HTMLButtonElement, LabeledSwitchProps>(
  ({ label, children, className, labelClassName, switchClassName, ...props }, ref) => {
    const switchProps = { ...props, ref };
    return (
      <div className={cn("relative", className)}>
        {label && (
          <label className={cn("inline-block mb-1 text-sm", labelClassName)} htmlFor={props.name}>
            {label}
          </label>
        )}
        <div>
          <Switch className={switchClassName} {...switchProps} />
          {children}
        </div>
      </div>
    );
  });
LabeledSwitch.displayName = "LabeledSwitch";
